import { useCallback, useEffect, useRef, useState } from 'react';
import { Track } from 'livekit-client';
// Deep import of the CommonJS build on purpose. This project is on CRA 4
// (webpack 4), which applies strict ESM rules to .mjs files and therefore
// refuses the package's ESM entry the moment it pulls a named export out of
// livekit-client's CJS bundle. Importing the CJS build directly sidesteps that
// without dragging in craco or an eject just for one dependency. The package
// declares no "exports" map, so this path is a supported one to reach.
import { BackgroundProcessor, supportsBackgroundProcessors } from '@livekit/track-processors/dist/index.js';

// Background blur and virtual backgrounds for the local camera, via LiveKit's
// own track processor (MediaPipe selfie segmentation behind an Insertable
// Streams pipeline).
//
// KNOWN LIMITATION - edge quality. The processor asks MediaPipe for a
// *category* mask (a hard 0-or-1 classification per pixel) and then tries to
// recover a soft edge from it in a shader. There is little to recover: a
// binary mask upscaled from 256x256 to a 540p frame gives a stair-stepped
// outline, which is why hair and shoulders look chewed. The fix is to use the
// model's *confidence* mask instead - the same inference, but its raw 0..1
// certainty, which is already soft exactly where the hard mask looks worst -
// and feather it before compositing. That needs a replacement transformer
// (the flags are hardcoded inside the library), which was attempted and is
// not finished; what is here is the version that works.
//
// The assets are served by this app rather than fetched from Google's CDN: the
// WASM is copied into public/ at build time (scripts/copy-mediapipe-assets.js)
// and the ~250KB model is committed next to it. A meeting that only works when
// a third-party CDN is reachable would be a meeting that breaks inside
// Electron behind a firewall, which is exactly where it gets used.
const ASSET_PATHS = {
    tasksVisionFileSet: `${process.env.PUBLIC_URL || ''}/mediapipe/wasm`,
    modelAssetPath: `${process.env.PUBLIC_URL || ''}/mediapipe/selfie_segmenter.tflite`
};

const DEFAULT_BLUR_RADIUS = 12;

// Not every browser can do this: the pipeline needs Insertable Streams
// (MediaStreamTrackProcessor), which Firefox and Safari still lack. Resolved
// once at module load because it cannot change within a session.
export const backgroundEffectsSupported = (() => {
    try {
        return supportsBackgroundProcessors();
    } catch (error) {
        return false;
    }
})();

// effect: { kind: 'none' } | { kind: 'blur' } | { kind: 'image', src }
const NO_EFFECT = { kind: 'none' };

export function useBackgroundEffect(room, cameraOn) {
    const [effect, setEffect] = useState(NO_EFFECT);
    const [applying, setApplying] = useState(false);
    const [error, setError] = useState(null);

    // One processor for the whole session, switched between modes rather than
    // rebuilt: switchTo exists precisely so changing background does not tear
    // down and re-seed the segmenter, which is visible as a stutter.
    const processorRef = useRef(null);
    // The effect the user asked for, read by the re-apply path below. A ref so
    // that re-applying after a camera toggle does not need this effect to
    // depend on (and therefore re-run for) every state change.
    const desiredRef = useRef(NO_EFFECT);
    desiredRef.current = effect;

    const localCameraTrack = useCallback(() => {
        if (!room) return null;
        const publication = room.localParticipant.getTrackPublication(Track.Source.Camera);
        return publication && publication.track ? publication.track : null;
    }, [room]);

    const apply = useCallback(async (next) => {
        const track = localCameraTrack();
        if (!track) return;

        if (next.kind === 'none') {
            if (processorRef.current) {
                await track.stopProcessor().catch(() => {});
                processorRef.current = null;
            }
            return;
        }

        const options = next.kind === 'blur'
            ? { mode: 'background-blur', blurRadius: DEFAULT_BLUR_RADIUS }
            : { mode: 'virtual-background', imagePath: next.src };

        // Switched in place rather than rebuilt: swapping processors tears the
        // segmenter down and seeds it again, which is visible as a stutter and
        // a frame or two of unmasked video.
        if (processorRef.current) {
            await processorRef.current.switchTo(options);
            return;
        }
        processorRef.current = BackgroundProcessor({ ...options, assetPaths: ASSET_PATHS });
        await track.setProcessor(processorRef.current);
    }, [localCameraTrack]);

    const selectEffect = useCallback(async (next) => {
        setEffect(next);
        setApplying(true);
        setError(null);
        try {
            await apply(next);
        } catch (err) {
            // Most likely cause is the model or WASM failing to load. Fall the
            // UI back to "off" rather than leaving a button lit for an effect
            // that is not actually running.
            setEffect(NO_EFFECT);
            setError(err && err.message ? err.message : 'failed');
            processorRef.current = null;
        } finally {
            setApplying(false);
        }
    }, [apply]);

    // Turning the camera off unpublishes the track, which takes the processor
    // with it - so a chosen effect has to be put back on the new track when the
    // camera comes back, or it would silently be lost on every toggle.
    useEffect(() => {
        if (!room || !cameraOn) {
            if (!cameraOn) processorRef.current = null;
            return;
        }
        if (desiredRef.current.kind === 'none') return;
        let cancelled = false;
        // A beat after the track is published: setProcessor on a track that is
        // still being negotiated throws.
        const timer = setTimeout(() => {
            if (cancelled) return;
            apply(desiredRef.current).catch(() => {});
        }, 150);
        return () => { cancelled = true; clearTimeout(timer); };
    }, [room, cameraOn, apply]);

    // The processor holds a worker and a GPU surface; dropping the reference
    // without stopping it leaks both for as long as the tab lives.
    useEffect(() => () => {
        const track = localCameraTrack();
        if (track && processorRef.current) track.stopProcessor().catch(() => {});
        processorRef.current = null;
    }, [localCameraTrack]);

    return { effect, selectEffect, applying, error, supported: backgroundEffectsSupported };
}
